/**
 * E06-F05-T14 — the short step-by-step explanation of how a research request
 * moves: discovery (free) → AI check (manual, paid) → scoring → review.
 *
 * Shown on the Run screen and on the AI check screen. Without it a finished run
 * read as "Ready for review · 0 of 891 scored" and the AI check screen (Volume /
 * Select first N / Run AI check) gave no hint WHY nothing was scored yet or what
 * the buttons cost. Static copy only — no data, no side effects.
 */
import type { JSX } from 'react';
import { RESEARCH_PROCESS_STEPS, type ResearchProcessStep } from './researchRunModel.js';

const STEP_COPY: Record<ResearchProcessStep, { title: string; text: string }> = {
  discovery: {
    title: 'Discovery (free)',
    text: 'Searches by industry × location find candidate companies. Nothing is scored yet — the tiles show what each search found.',
  },
  aicheck: {
    title: 'AI check (manual, paid)',
    text: 'On the AI check screen pick companies (Volume → Select first N, or tick rows) and press Run AI check. We read each website and the AI verifies the company. Some sites block automated access — those show Failed; open them by hand.',
  },
  scoring: {
    title: 'Scoring & ranking',
    text: 'Every checked company gets a grade and a score automatically. Unchecked companies stay unscored.',
  },
  review: {
    title: 'Review',
    text: 'Work through the long-list: scored companies first, unscored ones at the end.',
  },
};

export interface ResearchProcessStepsProps {
  /** The step the user is on right now (highlighted). */
  readonly current: ResearchProcessStep;
  readonly testId?: string;
}

export function ResearchProcessSteps({
  current,
  testId = 'research-process-steps',
}: ResearchProcessStepsProps): JSX.Element {
  return (
    <details
      open
      data-testid={testId}
      data-current-step={current}
      className="mb-6 rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-slate-700"
    >
      <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <i className="fa-solid fa-circle-info mr-1 text-blue-500" aria-hidden /> How research works
      </summary>
      <ol className="mt-2 grid gap-2 md:grid-cols-4">
        {RESEARCH_PROCESS_STEPS.map((step, i) => {
          const isCurrent = step === current;
          const copy = STEP_COPY[step];
          return (
            <li
              key={step}
              data-testid={`${testId}-${step}`}
              data-current={isCurrent ? 'true' : 'false'}
              className={`rounded border px-3 py-2 ${
                isCurrent ? 'border-[#D4AF37] bg-white shadow-sm' : 'border-transparent'
              }`}
            >
              <div className="mb-0.5 font-bold text-slate-800">
                {i + 1}. {copy.title}
                {isCurrent ? (
                  <span
                    data-testid={`${testId}-here-badge`}
                    className="ml-1 inline-block whitespace-nowrap rounded bg-[#D4AF37] px-1 text-[10px] text-slate-900"
                  >
                    you are here
                  </span>
                ) : null}
              </div>
              <p className="leading-snug text-slate-600">{copy.text}</p>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

export default ResearchProcessSteps;
